import { useEffect, useRef, useState } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { notificationService } from '@/api/services/notificationService'
import {
  applyNotificationRealtimeEvent,
  type NotificationRealtimeEvent,
} from '@/services/notifications/realtimeCache'

type Status = 'idle' | 'connecting' | 'connected' | 'disconnected'
const seenEvents = new Map<string, number>()
const CURSOR_KEY = 'notification_realtime_cursor'

const tokenIssuer = (token: string) => {
  try {
    return (JSON.parse(atob(token.split('.')[1])) as { iss?: string }).iss
  } catch {
    return undefined
  }
}

export const NotificationRealtimeProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const queryClient = useQueryClient()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const [status, setStatus] = useState<Status>('idle')
  const statusRef = useRef(status)

  useEffect(() => {
    statusRef.current = status
    localStorage.setItem('notification_realtime_status', status)
  }, [status])

  useEffect(() => {
    if (!isAuthenticated) return
    if (import.meta.env.VITE_NOTIFICATION_REALTIME_ENABLED === 'false') return
    const websocketUrl = import.meta.env.VITE_NOTIFICATION_WEBSOCKET_URL
    if (!websocketUrl) return

    let socket: WebSocket | undefined
    let stopped = false
    let reconnectAttempt = 0
    let reconnectTimer: number | undefined
    let heartbeatTimer: number | undefined

    const connect = async () => {
      setStatus('connecting')
      try {
        const session = await fetchAuthSession()
        const accessToken = session.tokens?.accessToken?.toString()
        const issuer = accessToken ? tokenIssuer(accessToken) : undefined
        if (!accessToken || !issuer || stopped) return
        const url = new URL(websocketUrl)
        url.searchParams.set('access_token', accessToken)
        url.searchParams.set('cognito_issuer', issuer)
        url.searchParams.set('entity', 'dispatcher')
        socket = new WebSocket(url.toString())

        socket.onopen = () => {
          reconnectAttempt = 0
          setStatus('connected')
          const since = Number(localStorage.getItem(CURSOR_KEY) || 0)
          void notificationService
            .getChanges(since)
            .then(result => {
              result.notifications.forEach(notification => {
                applyNotificationRealtimeEvent(queryClient, {
                  type: 'notification.updated',
                  event_id: String(
                    notification.event_id || notification.notification_id
                  ),
                  version: Number(notification.version || 1),
                  server_timestamp: Number(notification.server_timestamp || 0),
                  stage: String(notification.stage || ''),
                  notification,
                })
              })
              localStorage.setItem(CURSOR_KEY, String(result.cursor || since))
            })
            .catch(() =>
              queryClient.invalidateQueries({ queryKey: ['notifications'] })
            )
          heartbeatTimer = window.setInterval(() => {
            if (socket?.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ message: 'heartbeat' }))
            }
          }, 45_000)
        }
        socket.onmessage = message => {
          try {
            const event = JSON.parse(message.data) as NotificationRealtimeEvent
            if (!event.type?.startsWith('notification.') || !event.notification)
              return
            const previousVersion = seenEvents.get(event.event_id)
            if (
              previousVersion !== undefined &&
              previousVersion >= event.version
            )
              return
            seenEvents.set(event.event_id, event.version)
            applyNotificationRealtimeEvent(queryClient, event)
            localStorage.setItem(
              CURSOR_KEY,
              String(
                Math.max(
                  Number(localStorage.getItem(CURSOR_KEY) || 0),
                  event.server_timestamp
                )
              )
            )
          } catch {
            // Ignore messages owned by call/queue consumers on the shared API.
          }
        }
        socket.onclose = () => {
          window.clearInterval(heartbeatTimer)
          if (stopped) return
          setStatus('disconnected')
          const delay = Math.min(30_000, 1000 * 2 ** reconnectAttempt)
          reconnectAttempt += 1
          reconnectTimer = window.setTimeout(
            connect,
            delay + Math.random() * 500
          )
        }
      } catch {
        if (!stopped) {
          setStatus('disconnected')
          reconnectTimer = window.setTimeout(connect, 5_000)
        }
      }
    }

    void connect()
    const reconcileForegroundPush = () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
    const reconnectOnFocus = () => {
      if (!stopped && statusRef.current === 'disconnected') void connect()
    }
    window.addEventListener('online', reconnectOnFocus)
    window.addEventListener('focus', reconnectOnFocus)
    window.addEventListener('era:notification-fcm', reconcileForegroundPush)
    return () => {
      stopped = true
      window.clearTimeout(reconnectTimer)
      window.clearInterval(heartbeatTimer)
      window.removeEventListener('online', reconnectOnFocus)
      window.removeEventListener('focus', reconnectOnFocus)
      window.removeEventListener(
        'era:notification-fcm',
        reconcileForegroundPush
      )
      socket?.close()
      setStatus('idle')
    }
  }, [isAuthenticated, queryClient])

  return <>{children}</>
}
